import React, { useMemo, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from 'redux/hooks/reduxHooks';
import { selectIsAuth } from 'redux/selectors/selectors';
import { useGetProfileQuery } from 'redux/api/user.api';
import { Typography, Box, Stack } from '@mui/material';
import { useGetCurrentChallengeQuery } from 'redux/api/challenge.api';
import { useGetCurrentDebateQuery } from 'redux/api/debate.api';
import { PageContainer } from 'pages/PageContainer';
import VideoJSPlayer from 'components/VideoJSPlayer/VideoJSPlayer';
import { CardUserInfo } from 'components/Card/CardUserInfo/CardUserInfo';
import { ECardTypes } from 'shared/types/card.interface';
import { EChallengeStatus } from 'shared/types/challenges.interface';
import { CardMainButton } from 'components/Card/CardMainButton/CardMainButton';
import { useRole } from 'shared/hooks/useRole';
import { useNotFoundRedirect } from 'shared/guards/useNotFoundRedirect';
import { urls } from 'shared/constants/routing.constants';
import { CardMenuOpenButton } from 'components/Card/CardMenuOpenButton/CardMenuOpenButton';
import { ButtonShare } from 'components/Button/ButtonShare/ButtonShare';
import { opponentStyles } from 'shared/constants/mui-constants';
import DebateSkeleton from 'components/Skeletons/DebateSkeleton/DebateSkeleton';
import { FullCardDescription } from './FullCardDescription/FullCardDescription';

const FullCard: React.FC = () => {
  const { currentId } = useParams<{ currentId: string }>();
  const location = useLocation().pathname;
  const navigate = useNavigate();

  const {
    data: debate = null,
    isLoading: isDebateLoading,
    error: debateError,
  } = useGetCurrentDebateQuery(currentId, {
    pollingInterval: 1000,
    refetchOnMountOrArgChange: true,
    skip: !location.startsWith(urls.commonPages.debateCard),
  });
  const {
    data: challenge = null,
    isLoading: isChallengeLoading,
    error: challengeError,
  } = useGetCurrentChallengeQuery(currentId, {
    pollingInterval: 1000,
    refetchOnMountOrArgChange: true,
    skip: !location.startsWith(urls.commonPages.challengeCard),
  });
  useNotFoundRedirect(debateError || challengeError);

  const isAuth = useAppSelector(selectIsAuth);
  const { data: profileData = null } = useGetProfileQuery(undefined, { skip: !isAuth });

  const userRole = useRole(
    challenge?.initiator.id || debate?.challenge.initiator.id,
    challenge?.opponent.id || debate?.challenge.opponent.id,
    profileData?.id,
  );

  const cardData = useMemo(() => (debate ? debate.challenge : challenge!), [challenge, debate]);

  useEffect(() => {
    if (
      location.startsWith(urls.commonPages.challengeCard) &&
      challenge?.status === EChallengeStatus.Accepted &&
      challenge?.debate_id
    ) {
      const timer = setTimeout(() => {
        navigate(`${urls.commonPages.debateCard}/${challenge.debate_id}`);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [challenge, location, navigate, isChallengeLoading]);

  if (isDebateLoading || isChallengeLoading) {
    return (
      <PageContainer>
        <DebateSkeleton />
      </PageContainer>
    );
  }

  if (!debate && !challenge) {
    return (
      <PageContainer>
        <Typography>Debate or challenge not found</Typography>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Box>
        {debate && debate.recording_url ? (
          <VideoJSPlayer source={{ src: debate.recording_url }} />
        ) : (
          <Stack direction={{ md: 'row' }} columnGap="26px" rowGap="16px">
            <Stack sx={opponentStyles}>
              <CardUserInfo user={cardData.initiator} isFullCard />
            </Stack>
            <Stack sx={opponentStyles}>
              <CardUserInfo user={cardData.opponent} isFullCard>
                {challenge && challenge.status === EChallengeStatus.Waiting && (
                  <CardMainButton
                    type={ECardTypes.Challenge}
                    userRole={userRole}
                    status={cardData.status}
                    challengeId={cardData.id}
                  />
                )}
              </CardUserInfo>
            </Stack>
          </Stack>
        )}
        <FullCardDescription
          challengeData={cardData}
          debateData={debate}
          mt={{ lg: '52px', xs: '40px' }}
        >
          {debate && (
            <CardMainButton
              type={ECardTypes.Debate}
              userRole={userRole}
              status={debate.status}
              challengeId={cardData.id}
              debate={debate}
            />
          )}
          <ButtonShare shareUrl={window.location.href} title={cardData.thesis} />
          <CardMenuOpenButton
            type={debate ? ECardTypes.Debate : ECardTypes.Challenge}
            userRole={userRole}
            status={debate ? debate.status : cardData.status}
            id={debate ? debate.id : cardData.id}
          />
        </FullCardDescription>
      </Box>
    </PageContainer>
  );
};

export default FullCard;
